import { yupResolver } from '@hookform/resolvers/yup';
import { DumCommandApi } from '@src/Api/generated-global-event';
import { getInputDateFormat } from '@src/Common/Constants/getFormatDate';
import { useStores } from '@src/Common/Hooks/useStores';
import AxiosInstanceGlobalEvent from '@src/Common/Services/Api/AxiosInstanceGlobalEvent';
import { IReqBodyEvent } from '@src/Domain/DUMEvents/DUMEvents.type';
import { AxiosInstance } from 'axios';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { useAuth } from 'react-oidc-context';
import { IAddEventData } from './AddEventModal.type';
import { validationSchema } from './AddEventModal.validation';

const lCA = (AI: AxiosInstance) => new DumCommandApi(undefined, '', AI);
const useAddEventModal = (
  data: IAddEventData,
  show: boolean,
  existingId?: string
) => {
  const fetchCreateEvent = lCA(AxiosInstanceGlobalEvent(''));
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { isAuthenticated, signinSilent } = useAuth();
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    reset,
    register,
    setValue
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: data
  });

  const handleSubmitAdd = async () => {
    if (!isAuthenticated) {
      await signinSilent();
      UserStore.loadUserManager();
      return;
    }
    if (!getValues('latitude') || !getValues('longitude')) {
      return;
    }

    // check recaptcha invisible
    let captchaToken: string | null = null;
    if (recaptchaRef.current) {
      captchaToken = await recaptchaRef?.current.executeAsync();
      if (!captchaToken) {
        DUMEventStore.DUMEventDatas.status = {
          isSuccess: false,
          isLoading: false,
          isError: true,
          message: 'Unable to complete the CAPTCHA! Please reload the page'
        };
        return;
      }
    }
    const linkValue = getValues('link');
    const newReference =
      linkValue.startsWith('http://') || linkValue.startsWith('https://')
        ? linkValue
        : 'https://' + linkValue;

    const dataValue: IReqBodyEvent = {
      name: getValues('name'),
      eventDate: getValues('date'),
      reference: newReference,
      longitude: Number(getValues('longitude')),
      latitude: Number(getValues('latitude')),
      description: getValues('description'),
      captchaToken,
      existingId,
      // Optional. Left undefined when the user attaches nothing, so the request
      // carries no `banner` part at all and matches the previous behaviour.
      banner: getValues('banner') ?? undefined,
      // Optional and independent of `banner`. Undefined when blank so no
      // `banner_url` part is sent.
      bannerUrl: getValues('bannerUrl')?.trim() || undefined
    };

    const res = await DUMEventStore.createEvent(fetchCreateEvent, dataValue);
    if (res) {
      resetForm();
    }
  };

  const [isShow, setIsShow] = useState<boolean>(false);

  const { DUMEventStore, FormStore, UserStore, MySubmissionStore } =
    useStores();
  const { isSuccess } = DUMEventStore.DUMEventDatas.statusCreate;

  const resetForm = () => {
    setIsShow(false);
    FormStore.temporaryData = {
      name: '',
      date: '',
      description: '',
      link: '',
      latitude: '',
      longitude: '',
      banner: null,
      bannerUrl: ''
    };
    FormStore.clearCoordinate();
  };

  const handleClose = (cb: () => void) => {
    DUMEventStore.resetStatusCreate();
    resetForm();
    cb();
  };

  const handleCloseModal = async (onClose?: () => void) => {
    DUMEventStore.resetStatusCreate();
    if (isSuccess && onClose) {
      setIsShow(false);
      onClose();
    } else {
      if (!existingId) {
        setIsShow(true);
      }
    }
  };

  const handleViewMySubmission = () => {
    DUMEventStore.resetStatusCreate();
    setIsShow(false);
    MySubmissionStore.show = true;
    FormStore.isDetailVisible = false;
  };

  const handleNumber = (event: string) => {
    if (!isNaN(Number(event)) || event === '-' || event === '-.') {
      return event;
    }
    return '';
  };

  useEffect(() => {
    reset({
      ...FormStore.temporaryData,
      latitude:
        parseFloat(data.latitude) !== 0 ? Number(data.latitude).toFixed(6) : '',
      longitude:
        parseFloat(data.longitude) !== 0
          ? Number(data.longitude).toFixed(6)
          : ''
    });
    setIsShow(show);
    DUMEventStore.resetStatusCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (FormStore.selectedEvent !== null && FormStore.isDetailVisible) {
      setValue('name', FormStore.selectedEvent?.investigation || '');
      setValue(
        'date',
        getInputDateFormat(
          FormStore.selectedEvent?.date || '',
          false,
          false,
          '-'
        )
      );
      setValue('link', FormStore.selectedEvent?.reference || '');
      setValue(
        'latitude',
        FormStore.selectedEvent?.latlon[0]?.toString() || ''
      );
      setValue(
        'longitude',
        FormStore.selectedEvent?.latlon[1]?.toString() || ''
      );
      setValue('description', FormStore.selectedEvent?.description || '');
    }
  }, [FormStore.selectedEvent, FormStore.isDetailVisible, setValue]);

  return {
    isShow,
    setIsShow,
    handleClose,
    handleCloseModal,
    handleNumber,
    control,
    handleSubmit,
    getValues,
    errors,
    reset,
    handleSubmitAdd,
    recaptchaRef,
    handleViewMySubmission,
    register
  };
};

export default useAddEventModal;
